import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface JoinGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (inviteCode: string) => void;
  isLoading?: boolean;
  error?: string;
}

export function JoinGroupModal({ isOpen, onClose, onSubmit, isLoading = false, error }: JoinGroupModalProps) {
  const [inviteCode, setInviteCode] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isPasting, setIsPasting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!inviteCode.trim()) {
      setValidationError("Invite code is required");
      return;
    }

    // Basic validation - invite codes are typically alphanumeric
    if (!/^[a-zA-Z0-9]+$/.test(inviteCode.trim())) {
      setValidationError("Invalid invite code format");
      return;
    }

    if (inviteCode.trim().length < 6) {
      setValidationError("Invite code is too short");
      return;
    }

    if (inviteCode.trim().length > 50) {
      setValidationError("Invite code is too long");
      return;
    }

    setValidationError("");
    onSubmit(inviteCode.trim());
  };

  const handleClose = () => {
    if (!isLoading) {
      setInviteCode("");
      setValidationError("");
      onClose();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Skip processing if this is a paste event (handled separately)
    if (isPasting) {
      setIsPasting(false);
      return;
    }
    setInviteCode(e.target.value.toUpperCase()); // Convert to uppercase for consistency
    setValidationError("");
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault(); // Prevent default paste behavior
    const pastedText = e.clipboardData.getData('text');
    // Clean and format the pasted text
    const cleanedText = pastedText.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    setIsPasting(true);
    setInviteCode(cleanedText);
    setValidationError("");

    // Reset the pasting flag after a brief delay
    setTimeout(() => setIsPasting(false), 10);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Join Group</DialogTitle>
          <DialogDescription>
            Enter the invite code provided by your group admin to join their group.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="inviteCode">Invite Code</Label>
            <Input
              id="inviteCode"
              type="text"
              placeholder="Enter invite code"
              value={inviteCode}
              onChange={handleInputChange}
              onPaste={handlePaste}
              disabled={isLoading}
              maxLength={50}
              autoFocus
              className="font-mono text-center text-lg tracking-wider"
            />
            <p className="text-xs text-muted-foreground">
              Ask your group admin for the invite code
            </p>
          </div>

          {(validationError || error) && (
            <Alert variant="destructive">
              <AlertDescription>
                {validationError || error}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !inviteCode.trim() || inviteCode.trim().length < 6}
              className="flex-1"
            >
              {isLoading ? "Joining..." : "Join Group"}
            </Button>
          </div>
        </form>

        <div className="text-xs text-muted-foreground pt-4 border-t space-y-2">
          <p><strong>Note:</strong></p>
          <ul className="list-disc list-inside space-y-1">
            <li>You can only be a member of one group at a time</li>
            <li>Invite codes are controlled by group admins</li>
            <li>You'll be able to generate tokens for any group member</li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}